import React, { useEffect, useMemo, useState } from 'react';
import PropTypes from 'prop-types';
import cls from 'classnames';

const AutoComplete = ({
  name,
  open,
  items,
  value,
  onSelect,
  onChange,
  renderMenu,
  renderItem,
  getItemValue,
  inputProps,
  styles,
  unstyled,
  shouldItemRender,
  renderItemStyle,
  onMenuVisibilityChange,
}) => {
  const [isOpen, setIsOpen] = useState(!!open);
  const [highlightedIndex, setHighlightedIndex] = useState(null);

  useEffect(() => {
    setIsOpen(!!open);
  }, [open]);

  useEffect(() => {
    if (!isOpen) setHighlightedIndex(null);
    onMenuVisibilityChange(isOpen);
  }, [isOpen]);

  const filteredItems = useMemo(() => {
    let filtered = [];
    if (items && Array.isArray(items)) {
      filtered = [...items].filter((item) => shouldItemRender(item, value));
    }
    return filtered;
  }, [items, value]);

  const handleSelect = (item) => {
    const val = getItemValue ? getItemValue(item) : item;
    onSelect(val, item);
    setHighlightedIndex(null);
  };

  const handleKeyDown = (e) => {
    if (!isOpen) return;
    const last = filteredItems.length - 1;
    switch (e.key) {
      case 'ArrowDown':
        e.preventDefault();
        setHighlightedIndex((prev) => (prev === null || prev === last ? 0 : prev + 1));
        break;
      case 'ArrowUp':
        e.preventDefault();
        setHighlightedIndex((prev) => (prev === null || prev === 0 ? last : prev - 1));
        break;
      case 'Enter':
        e.preventDefault();
        if (highlightedIndex !== null && filteredItems[highlightedIndex]) {
          handleSelect(filteredItems[highlightedIndex]);
        }
        break;
      case 'Escape':
        setIsOpen(false);
        e.target.blur();
        break;
      default:
        break;
    }
  };

  const renderInput = (
    <input
      id={`${name}-input`}
      type="text"
      value={value}
      className={cls({ 'w-full text-4 leading-5 text-dark tracking-0.2 placeholder-dark focus:outline-none': !unstyled }, styles?.input)}
      onChange={(e) => onChange(e.target.value, e)}
      onKeyDown={handleKeyDown}
      autoComplete="off"
      // eslint-disable-next-line react/jsx-props-no-spreading
      {...inputProps}
    />
  );

  const renderSuggestedItem = (item, i) => {
    const key = `autocomplete-${name}-${item?.id || i}`;
    if (renderItem) return renderItem(item, highlightedIndex === i);

    return (
      <li
        role="option"
        key={key}
        className={cls({ 'py-4 pl-8 pr-24 text-left cursor-pointer flex items-center': !unstyled }, styles?.item, renderItemStyle(item, highlightedIndex === i))}
        tabIndex={0}
        onMouseDown={(e) => {
          e.preventDefault();
          handleSelect(item);
        }}
        onMouseEnter={() => setHighlightedIndex(i)}
        aria-selected={highlightedIndex === i}
      >
        {getItemValue ? getItemValue(item) : item}
      </li>
    );
  };

  const renderSuggestionMenu = renderMenu ? renderMenu(filteredItems, value) : (
    <ul
      id={`${name}-menu`}
      role="listbox"
      className={cls({ 'mt-2 list-none bg-white rounded-3 absolute left-0 top-full z-100': !unstyled }, styles?.menu)}
    >
      {filteredItems.map((item, i) => renderSuggestedItem(item, i))}
    </ul>
  );

  return (
    <div
      className={cls({ 'w-full': !unstyled }, styles?.wrapper)}
      role="combobox"
      aria-autocomplete="list"
      aria-expanded={isOpen}
      aria-controls={`${name}-menu`}
    >
      {renderInput}
      {isOpen && filteredItems.length > 0 && renderSuggestionMenu}
    </div>
  );
};

AutoComplete.defaultProps = {
  name: '',
  open: false,
  items: [],
  value: '',
  renderMenu: null,
  renderItem: null,
  inputProps: {},
  styles: {},
  unstyled: false,
  shouldItemRender: () => true,
  getItemValue: (item) => item,
  onSelect: () => null,
  onChange: () => null,
  renderItemStyle: () => ({}),
  onMenuVisibilityChange: () => null,
};

AutoComplete.propTypes = {
  name: PropTypes.string,
  open: PropTypes.bool,
  items: PropTypes.array,
  value: PropTypes.string,
  renderMenu: PropTypes.func,
  renderItem: PropTypes.func,
  inputProps: PropTypes.object,
  styles: PropTypes.object,
  unstyled: PropTypes.bool,
  shouldItemRender: PropTypes.func,
  getItemValue: PropTypes.func,
  onChange: PropTypes.func,
  onSelect: PropTypes.func,
  renderItemStyle: PropTypes.func,
  onMenuVisibilityChange: PropTypes.func,
};

export default AutoComplete;
